import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    Optional,
    Renderer2,
} from '@angular/core';
import {
    AbstractControl,
    NG_VALIDATORS,
    ValidationErrors,
    Validator,
} from '@angular/forms';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'kendo-textarea',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: MaxLengthTextareaDirective,
            multi: true
        }
    ],
    standalone: false
})
export class MaxLengthTextareaDirective implements Validator, AfterViewInit {
    readonly defaultMaxLength: number = 1000;
    @Input() textareaMaxlength: number = this.defaultMaxLength;
    @Input() showCharCount: boolean = false;

    private metaRow: HTMLElement | null = null;
    private errorText: HTMLElement | null = null;
    private counterText: HTMLElement | null = null;

    constructor(
        @Optional() private readonly elementRef: ElementRef<HTMLElement> | null = null,
        @Optional() private readonly renderer: Renderer2 | null = null,
    ) { }

    ngAfterViewInit(): void {
        if (!this.elementRef || !this.renderer) {
            return;
        }

        this.createMetaRow();
        this.updateMeta(null);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!control?.value) {
            this.updateMeta(control?.value);
            return null;
        }

        const valueLength = String(control.value).length;
        this.updateMeta(control.value);

        return valueLength > this.textareaMaxlength
            ? { maxlength: { requiredLength: this.textareaMaxlength, actualLength: valueLength } }
            : null;
    }

    private createMetaRow(): void {
        const host = this.elementRef?.nativeElement;
        const parent = host?.closest('kendo-formfield');

        if (!host || !parent || !this.renderer || this.metaRow) {
            return;
        }

        this.metaRow = this.renderer.createElement('div');
        this.renderer.setStyle(this.metaRow, 'display', 'flex');
        this.renderer.setStyle(this.metaRow, 'justify-content', this.showCharCount ? 'space-between' : 'flex-start');
        this.renderer.setStyle(this.metaRow, 'align-items', 'center');
        this.renderer.setStyle(this.metaRow, 'font-size', '12px');
        this.renderer.setStyle(this.metaRow, 'margin-top', '4px');

        this.errorText = this.renderer.createElement('kendo-formerror');
        this.renderer.setStyle(this.errorText, 'color', '#ec002f');

        this.renderer.appendChild(this.metaRow, this.errorText);

        if (this.showCharCount) {
            this.counterText = this.renderer.createElement('kendo-formerror');
            this.renderer.setStyle(this.counterText, 'margin-left', 'auto');
            this.renderer.appendChild(this.metaRow, this.counterText);
        }

        this.renderer.appendChild(parent, this.metaRow);
    }

    private updateMeta(value: unknown): void {
        if (!this.renderer || !this.errorText) {
            return;
        }

        const currentLength = value ? String(value).length : 0;
        const isExceeded = currentLength > this.textareaMaxlength;

        if (this.showCharCount && this.counterText) {
            this.renderer.setProperty(
                this.counterText,
                'textContent',
                `${currentLength}/${this.textareaMaxlength}`,
            );
        }

        this.renderer.setProperty(
            this.errorText,
            'textContent',
            isExceeded
                ? `Error: Maximum length of ${this.textareaMaxlength} characters exceeded`
                : '',
        );
    }
}
